import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createAnnotation, resolveAnnotation } from '../api/client.js';
import { hashPatch } from '../lib/patchHash.js';
import { rangeToSourceOffsets, resolveAnchor, segmentAnnotationIntervals } from '../lib/annotationAnchor.js';
import type { AnnotationLocator, AnnotationView } from '../types/workflow.js';

export interface AnnotatableField {
  key: string;
  label: string;
  locator: AnnotationLocator;
  text: string;
}

export function annotationFields(patch: Record<string, unknown>): AnnotatableField[] {
  const fields: AnnotatableField[] = [];
  for (const field of ['title', 'summary'] as const) {
    if (typeof patch[field] === 'string') fields.push({ key: `entity:${field}`, label: field === 'title' ? 'Title' : 'Summary', locator: { scope: 'entity', field }, text: patch[field] });
  }
  const steps = Array.isArray(patch.steps) ? patch.steps : [];
  steps.forEach((candidate, index) => {
    if (!isRecord(candidate) || !isId(candidate.id)) return;
    for (const field of ['prompt', 'text'] as const) {
      if (typeof candidate[field] === 'string') fields.push({ key: `step:${String(candidate.id)}:${field}`, label: `Step ${index + 1} ${field}`, locator: { scope: 'step', id: candidate.id as string | number, field }, text: candidate[field] });
    }
  });
  const responses = Array.isArray(patch.responses) ? patch.responses : [];
  responses.forEach((candidate, index) => {
    if (!isRecord(candidate) || !isId(candidate.id)) return;
    for (const field of ['value', 'feedback'] as const) {
      if (typeof candidate[field] === 'string') fields.push({ key: `response:${String(candidate.id)}:${field}`, label: `Response ${index + 1} ${field}`, locator: { scope: 'response', id: candidate.id as string | number, field }, text: candidate[field] });
    }
  });
  return fields;
}

export function annotationCount(annotations: AnnotationView[], locator: AnnotationLocator): number {
  return annotations.filter((annotation) => annotation.status === 'open' && sameLocator(annotation.anchor.locator, locator)).length;
}

export function AnnotationDrawer({
  annotations,
  dirty,
  onAnnotationsChange,
  onDiscard,
  onError,
  patch,
  readOnly = false,
  workItemId,
}: {
  annotations: AnnotationView[];
  dirty: boolean;
  onAnnotationsChange: (annotations: AnnotationView[]) => void;
  onDiscard: () => void;
  onError: (message: string) => void;
  patch: Record<string, unknown>;
  readOnly?: boolean;
  workItemId: string;
}) {
  const fields = useMemo(() => annotationFields(patch), [patch]);
  const patchHash = useMemo(() => hashPatch(patch), [patch]);
  const [selection, setSelection] = useState<{ field: AnnotatableField; startOffset: number; endOffset: number; source: HTMLElement } | null>(null);
  const [comment, setComment] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!selection) return;
    commentRef.current?.focus();
  }, [selection]);

  if (dirty) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <p>Annotations attach to the saved draft. Discard your unsaved edits to annotate, or approve with edits first.</p>
        <button className="mt-3 rounded bg-amber-800 px-3 py-1.5 text-xs font-bold text-white" onClick={onDiscard} type="button">Discard local edits</button>
      </div>
    );
  }

  function captureSelection(root: HTMLElement, field: AnnotatableField) {
    if (readOnly) return;
    const range = window.getSelection()?.rangeCount ? window.getSelection()!.getRangeAt(0) : null;
    if (!range || range.collapsed) return;
    const offsets = rangeToSourceOffsets(root, range);
    if (!offsets || offsets.endOffset - offsets.startOffset > 500) return;
    setSelection({ field, ...offsets, source: root });
    setComment('');
  }

  async function submit() {
    if (!selection || !comment.trim() || busy) return;
    setBusy(true);
    onError('');
    try {
      const { field, startOffset, endOffset } = selection;
      const created = await createAnnotation(workItemId, {
        annotationId: createUlid(), expectedPatchHash: patchHash,
        anchor: {
          locator: field.locator,
          quote: field.text.slice(startOffset, endOffset),
          prefix: field.text.slice(Math.max(0, startOffset - 32), startOffset),
          suffix: field.text.slice(endOffset, endOffset + 32),
          startOffset, endOffset,
        },
        comment: comment.trim(),
      });
      onAnnotationsChange([...annotations, created]);
      setActiveId(created.annotationId);
      setSelection(null);
      setComment('');
      selection.source.focus();
      window.getSelection()?.removeAllRanges();
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Unable to create annotation.');
    } finally {
      setBusy(false);
    }
  }

  async function resolveCurrent(annotation: AnnotationView) {
    if (busy) return;
    setBusy(true);
    onError('');
    try {
      await resolveAnnotation(workItemId, annotation.annotationId);
      onAnnotationsChange(annotations.map((candidate) => candidate.annotationId === annotation.annotationId ? { ...candidate, status: 'resolved' } : candidate));
      setActiveId(null);
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Unable to resolve annotation.');
    } finally {
      setBusy(false);
    }
  }

  function handleDialogKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      const source = selection?.source;
      setSelection(null);
      source?.focus();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('textarea,button') ?? [])];
    if (!focusable.length) return;
    const current = focusable.indexOf(document.activeElement as HTMLElement);
    const next = event.shiftKey ? (current <= 0 ? focusable.length - 1 : current - 1) : (current + 1) % focusable.length;
    event.preventDefault();
    focusable[next]?.focus();
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">{readOnly ? 'This draft is no longer pending. Existing annotations are read-only, but may still be resolved.' : 'Select text within one saved-draft field to attach a comment.'}</p>
      {fields.map((field) => {
        const open = annotations.filter((annotation) => annotation.status === 'open' && sameLocator(annotation.anchor.locator, field.locator));
        const resolved = open.map((annotation) => ({ annotation, resolution: resolveAnchor(annotation.anchor, patch, patchHash, annotation.patchHash) }));
        const anchored = resolved.filter((entry) => entry.resolution.status !== 'stale');
        const stale = resolved.filter((entry) => entry.resolution.status === 'stale');
        const intervals = anchored.map(({ annotation, resolution }) => ({
          annotationId: annotation.annotationId,
          startOffset: 'startOffset' in resolution ? resolution.startOffset : 0,
          endOffset: 'endOffset' in resolution ? resolution.endOffset : 0,
        }));
        return (
          <section className="rounded-lg border border-slate-200 p-3" key={field.key}>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-slate-500">
              <span>{field.label}</span>
              {open.length ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">{open.length}</span> : null}
            </div>
            <div
              aria-label={`${field.label} annotation source`}
              className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-amber-400"
              onKeyUp={(event) => captureSelection(event.currentTarget, field)}
              onMouseUp={(event) => captureSelection(event.currentTarget, field)}
              role="textbox"
              tabIndex={0}
            >
              {segmentAnnotationIntervals(field.text, intervals).map((segment) => segment.annotationIds.length ? (
                <mark
                  className="cursor-pointer rounded bg-amber-200 px-0.5"
                  data-annotation-ids={segment.annotationIds.join(',')}
                  key={`${segment.startOffset}-${segment.endOffset}`}
                  onClick={() => setActiveId(segment.annotationIds[0] ?? null)}
                >{segment.text}</mark>
              ) : <span key={`${segment.startOffset}-${segment.endOffset}`}>{segment.text}</span>)}
            </div>
            {stale.length ? (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2">
                <p className="text-xs font-bold text-rose-800">Needs re-placement</p>
                {stale.map(({ annotation }) => <button className="mt-1 block text-left text-xs text-rose-900 underline" key={annotation.annotationId} onClick={() => setActiveId(annotation.annotationId)} type="button">“{annotation.anchor.quote}”</button>)}
              </div>
            ) : null}
            {open.filter((annotation) => annotation.annotationId === activeId).map((annotation) => (
              <article className="mt-2 rounded-lg border border-amber-200 bg-white p-3 text-sm shadow-sm" key={annotation.annotationId}>
                <p className="font-semibold text-slate-800">“{annotation.anchor.quote}”</p>
                <p className="mt-1 whitespace-pre-wrap text-slate-700">{annotation.comment}</p>
                <p className="mt-2 text-xs text-slate-500">{annotation.author.email} · {new Date(annotation.timestamp).toLocaleString()}</p>
                <button className="mt-2 rounded bg-slate-800 px-2.5 py-1 text-xs font-bold text-white" disabled={busy} onClick={() => void resolveCurrent(annotation)} type="button">Resolve</button>
              </article>
            ))}
          </section>
        );
      })}
      {selection ? (
        <div aria-label="Add annotation" aria-modal="true" className="sticky bottom-2 rounded-xl border border-amber-300 bg-white p-3 shadow-xl" onKeyDown={handleDialogKeyDown} ref={dialogRef} role="dialog">
          <p className="text-xs font-bold text-slate-700">Add comment for “{selection.field.text.slice(selection.startOffset, selection.endOffset)}”</p>
          <textarea aria-label="Annotation comment" className="mt-2 w-full rounded border border-slate-300 p-2 text-sm" maxLength={2000} onChange={(event) => setComment(event.target.value)} ref={commentRef} rows={3} value={comment} />
          <div className="mt-2 flex justify-end gap-2">
            <button className="px-2 py-1 text-xs font-bold text-slate-500" onClick={() => { const source = selection.source; setSelection(null); source.focus(); }} type="button">Cancel</button>
            <button className="rounded bg-amber-700 px-3 py-1.5 text-xs font-bold text-white" disabled={busy || !comment.trim()} onClick={() => void submit()} type="button">Add comment</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function sameLocator(left: AnnotationLocator, right: AnnotationLocator): boolean {
  return left.scope === right.scope && left.field === right.field &&
    (left.scope === 'entity' || right.scope === 'entity' || String(left.id) === String(right.id));
}

function createUlid(): string {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let time = Date.now();
  let result = '';
  for (let index = 0; index < 10; index += 1) {
    result = alphabet[time % 32]! + result;
    time = Math.floor(time / 32);
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  for (let index = 0; index < 16; index += 1) result += alphabet[bytes[index]! % 32];
  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isId(value: unknown): value is string | number {
  return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));
}
